/**
 * Cumulative XP — account-level progression.
 *
 * Deliberately a different thing from the in-match level in `@/game/levels`,
 * and the two are easy to confuse, so: a **level** is how one round went and
 * resets with the next deal; **XP** is everything this account has ever done
 * and only goes up. The HUD chip says "LV 5" and means the round; the rank chip
 * says "XP LV 12" and means the account.
 *
 * Shared across Chain Games titles. The thresholds, the rank names, the spans
 * and the award parts are identical in every title on purpose — a badge has to
 * mean the same thing wherever somebody sees it, so none of these numbers may
 * be tuned per game. The one part that is allowed to differ is `clears`, which
 * counts each game's own repeatable unit of progress: Blockari counts lines,
 * 21 Wild counts piles cleared.
 *
 * Pure, like every other rules module here, because the server replays it. The
 * client never posts an XP figure and there is no field for it to post one in.
 */

/** Scales the XP ladder. Shared; do not tune per title. */
export const XP_BASE = 150;

/** What each part of a finished round is worth. Shared; do not tune. */
export const XP_RATES = {
  /** For finishing at all — solo included. */
  played: 50,
  /** Per this many points of final score. */
  scorePer: 50,
  /** Per pile cleared: 21 Wild's unit of progress. */
  clear: 10,
  /** Per in-match level beyond the first. */
  perLevel: 25,
  /**
   * Once per round for a big streak.
   *
   * Blockari pays this for a 4X. Our ladder is labelled 2x..6x at streak levels
   * 1..5, so a 4X is level 3 — the same callout by name, which is what a player
   * comparing the two games will actually check.
   */
  streak: 40,
  streakAt: 3,
  /** For playing a challenge at all, win or lose. */
  challenge: 25,
  /** To the winner, at settlement, plus half the pot (up to `potCap`). */
  win: 100,
  /**
   * The most of a pot that earns XP: 200 = the pot of the old top rung (a 100 stake).
   * Stakes are a custom amount now, and a win at any larger pot pays the same XP as a
   * 100 stake, so a big wager (two accounts passing a large stake back and forth) can
   * never be turned into rank. Blockari's rule (`xpPotCap`).
   */
  potCap: 200,
} as const;

export interface XpParts {
  played: number;
  score: number;
  /** Named `lines` in Blockari; the same slot, counting our own unit. */
  clears: number;
  levels: number;
  streak: number;
  challenge: number;
  win: number;
}

export function emptyParts(): XpParts {
  return { played: 0, score: 0, clears: 0, levels: 0, streak: 0, challenge: 0, win: 0 };
}

export function totalOf(parts: XpParts): number {
  return (
    parts.played + parts.score + parts.clears + parts.levels + parts.streak + parts.challenge + parts.win
  );
}

export interface FinishedRound {
  /** Final total, bonuses included. */
  score: number;
  /** Piles cleared. */
  clears: number;
  /** In-match level reached. */
  level: number;
  /** Highest streak level, 0..5. */
  bestStreak: number;
  /** Cards played. Zero earns nothing at all. */
  plays: number;
  /** Whether this round was played on a challenge. */
  challenge: boolean;
}

/**
 * What a finished round is worth.
 *
 * The `plays === 0` guard is the whole anti-farming rule and it is worth being
 * explicit about: without it, opening a challenge and letting the clock expire
 * pays 50 XP for nothing, which makes doing exactly that the cheapest
 * progression in the game. A player who made a single move gets `played` and
 * whatever that move earned; a player who made none gets nothing.
 */
export function awardForRound(round: FinishedRound): XpParts {
  const parts = emptyParts();
  if (!Number.isFinite(round.plays) || round.plays <= 0) return parts;

  parts.played = XP_RATES.played;
  parts.score = Math.floor(Math.max(0, round.score) / XP_RATES.scorePer);
  parts.clears = Math.max(0, Math.floor(round.clears)) * XP_RATES.clear;
  parts.levels = Math.max(0, Math.floor(round.level) - 1) * XP_RATES.perLevel;
  parts.streak = round.bestStreak >= XP_RATES.streakAt ? XP_RATES.streak : 0;
  parts.challenge = round.challenge ? XP_RATES.challenge : 0;
  return parts;
}

/** What winning a challenge is worth, on top of the round itself. */
export function awardForWin(pot: number): XpParts {
  const parts = emptyParts();
  const counted = Number.isFinite(pot) ? Math.min(Math.max(0, pot), XP_RATES.potCap) : 0;
  parts.win = XP_RATES.win + Math.floor(counted / 2);
  return parts;
}

/** Add two awards, for a result that folds a round and its settlement. */
export function addParts(a: XpParts, b: XpParts): XpParts {
  return {
    played: a.played + b.played,
    score: a.score + b.score,
    clears: a.clears + b.clears,
    levels: a.levels + b.levels,
    streak: a.streak + b.streak,
    challenge: a.challenge + b.challenge,
    win: a.win + b.win,
  };
}

/* ------------------------------------------------------------------ *
 * The ladder
 * ------------------------------------------------------------------ */

/** Total XP needed to reach XP level `n`. */
export function xpThreshold(n: number): number {
  if (!Number.isFinite(n) || n <= 1) return 0;
  return XP_BASE * (n - 1) * (n + 3);
}

/**
 * The XP level a total has reached.
 *
 * Same shape and same boundary walk as `levelFor` — see the note there for why
 * the closed form alone is not trusted at a threshold.
 */
export function xpLevelFor(xp: number): number {
  if (!Number.isFinite(xp) || xp <= 0) return 1;

  let n = Math.floor(Math.sqrt(4 + xp / XP_BASE) - 1);
  if (n < 1) n = 1;
  while (xpThreshold(n + 1) <= xp) n++;
  while (n > 1 && xpThreshold(n) > xp) n--;
  return n;
}

/* ------------------------------------------------------------------ *
 * Ranks
 * ------------------------------------------------------------------ */

/**
 * Nine named ranks, five XP levels each, then roman numerals forever.
 *
 * **The deck, ascending.** Adam's call: each title names its ranks after its own
 * material, so Blockari climbs stonework (Pebble → Legend) and 21 Wild climbs
 * the cards. The ordering needs no explaining to anybody who has played a hand
 * here — it is the card ranking they are already using — and the top rank is
 * the wild card, which is both the most powerful thing in the game and the
 * thing the game is named after.
 *
 * The CURVE is unchanged and still shared: level 12 costs the same effort in
 * either title, so "I hit 12" compares. Only the badge's name is ours.
 */
export const RANK_NAMES = [
  'Deuce',
  'Seven',
  'Ten',
  'Jack',
  'Queen',
  'King',
  'Ace',
  'Blackjack',
  'Wild',
] as const;

/** XP levels inside one rank. */
export const RANK_SPAN = 5;

export interface Rank {
  /** 0..8, the index into RANK_NAMES. Clamped at Legend. */
  index: number;
  /** "Mason", or "Legend II" once past the ninth rank. */
  name: string;
  /** 1..5 within the rank. */
  tier: number;
}

/** Roman numerals for the Legend repeats. Small by construction — it is one per
 *  five levels past 45, and a player at Legend X has 100 XP levels. */
function roman(n: number): string {
  const table: Array<[number, string]> = [
    [10, 'X'],
    [9, 'IX'],
    [5, 'V'],
    [4, 'IV'],
    [1, 'I'],
  ];
  let left = n;
  let out = '';
  for (const [value, glyph] of table) {
    while (left >= value) {
      out += glyph;
      left -= value;
    }
  }
  return out;
}

export function rankFor(level: number): Rank {
  const n = Number.isFinite(level) ? Math.max(1, Math.floor(level)) : 1;
  const step = Math.floor((n - 1) / RANK_SPAN);
  const index = Math.min(step, RANK_NAMES.length - 1);
  const tier = ((n - 1) % RANK_SPAN) + 1;

  // Past the last named rank the name repeats with a numeral: Legend, then
  // Legend II, Legend III. The first repeat is plain "Legend", not "Legend I".
  const past = step - (RANK_NAMES.length - 1);
  const base = RANK_NAMES[index]!;
  const name = past > 0 ? `${base} ${roman(past + 1)}` : base;

  return { index, name, tier };
}

/** The tier as a numeral, for "MASON III". */
export function tierNumeral(tier: number): string {
  return roman(Math.max(1, Math.min(RANK_SPAN, Math.floor(tier))));
}

/** How far through the current XP level a total is, 0..1. */
export function xpProgress(xp: number): number {
  const level = xpLevelFor(xp);
  const floor = xpThreshold(level);
  const ceiling = xpThreshold(level + 1);
  if (ceiling <= floor) return 0;
  return Math.min(1, Math.max(0, (xp - floor) / (ceiling - floor)));
}
